import { Fragment, type ReactNode } from "react";

/**
 * Bionic-style reading: bold the leading portion of each word so the eye fixates
 * faster and skims less. Opt-in (some readers love it, some don't). The split
 * point is pure + tested; rendering wraps the lead in <b> and keeps the rest.
 */

/** Split a word into [lead, rest] where lead is the bolded fixation part. */
export function bionicSplit(word: string): [string, string] {
  const len = word.length;
  if (len <= 1) return [word, ""];
  // Shorter words get a bigger proportion bolded; ~40% with a floor of 1.
  const lead = len <= 3 ? 1 : Math.ceil(len * 0.42);
  return [word.slice(0, lead), word.slice(lead)];
}

/** Render text as bionic nodes (preserves whitespace + punctuation positions). */
export function bionicNodes(text: string): ReactNode {
  // Split on whitespace boundaries, keeping the separators so spacing is intact.
  const parts = text.split(/(\s+)/);
  return parts.map((part, i) => {
    if (!part || /^\s+$/.test(part)) return <Fragment key={i}>{part}</Fragment>;
    // Bold leading letters; keep any trailing punctuation in the "rest".
    const match = part.match(/^([\p{L}\p{N}]+)(.*)$/u);
    if (!match) return <Fragment key={i}>{part}</Fragment>;
    const [, coreWord, trailing] = match;
    const [lead, rest] = bionicSplit(coreWord);
    return (
      <Fragment key={i}>
        <b className="bionic-lead">{lead}</b>
        {rest}
        {trailing}
      </Fragment>
    );
  });
}
